import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  podcastEpisodesApi,
  type PodcastEpisode,
  type UpsertEpisodePayload,
} from "../api/podcastEpisodesApi";

const QUERY_KEY = ["admin-podcast-episodes"];

const emptyForm = (): UpsertEpisodePayload => ({
  title: "",
  description: "",
  guest_name: "",
  guest_title: "",
  episode_number: null,
  thumbnail_url: "",
  spotify_url: "",
  youtube_url: "",
  topics: [],
  is_active: true,
  display_order: 0,
});

const episodeToForm = (ep: PodcastEpisode): UpsertEpisodePayload => ({
  title: ep.title,
  description: ep.description ?? "",
  guest_name: ep.guest_name ?? "",
  guest_title: ep.guest_title ?? "",
  episode_number: ep.episode_number,
  thumbnail_url: ep.thumbnail_url ?? "",
  spotify_url: ep.spotify_url ?? "",
  youtube_url: ep.youtube_url ?? "",
  topics: ep.topics,
  is_active: ep.is_active,
  display_order: ep.display_order,
});

// ---------------------------------------------------------------------------
// EpisodeCard
// ---------------------------------------------------------------------------

function EpisodeCard({
  ep,
  onEdit,
  onDelete,
  onToggle,
  toggling,
  deleting,
}: {
  ep: PodcastEpisode;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: (active: boolean) => void;
  toggling: boolean;
  deleting: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        p: 1.75,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        transition: "border-color 0.15s",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      {/* Thumbnail */}
      <Avatar
        src={ep.thumbnail_url ?? undefined}
        variant="rounded"
        sx={{ width: 56, height: 56, flexShrink: 0, bgcolor: "action.hover", fontSize: 14, fontWeight: 700 }}
      >
        {ep.episode_number != null ? ep.episode_number : <PodcastsIcon fontSize="small" />}
      </Avatar>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={600} noWrap>
          {ep.episode_number != null ? `Ep ${ep.episode_number} · ` : ""}{ep.title}
        </Typography>

        {ep.guest_name && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {ep.guest_name}{ep.guest_title ? ` · ${ep.guest_title}` : ""}
          </Typography>
        )}

        {ep.topics.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75 }}>
            {ep.topics.slice(0, 4).map((t) => (
              <Chip key={t} label={t} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
            ))}
            {ep.topics.length > 4 && (
              <Chip label={`+${ep.topics.length - 4}`} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
            )}
          </Stack>
        )}

        {/* Links row */}
        {(ep.spotify_url || ep.youtube_url) && (
          <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
            {ep.spotify_url && (
              <Typography
                component="a"
                href={ep.spotify_url}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                color="primary"
                sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                Spotify
              </Typography>
            )}
            {ep.youtube_url && (
              <Typography
                component="a"
                href={ep.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                color="primary"
                sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                YouTube
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      {/* Right side: status + actions */}
      <Stack alignItems="flex-end" spacing={0.75} sx={{ flexShrink: 0 }}>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={onEdit}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" disabled={deleting} onClick={onDelete}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Tooltip title={ep.is_active ? "Deactivate" : "Activate"}>
          <Chip
            label={ep.is_active ? "Active" : "Inactive"}
            size="small"
            color={ep.is_active ? "success" : "default"}
            variant={ep.is_active ? "filled" : "outlined"}
            onClick={() => !toggling && onToggle(!ep.is_active)}
            sx={{ cursor: "pointer", fontSize: 11, height: 22 }}
          />
        </Tooltip>
        {ep.display_order > 0 && (
          <Typography variant="caption" color="text.disabled">
            #{ep.display_order}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// EpisodeDialog
// ---------------------------------------------------------------------------

interface EpisodeDialogProps {
  open: boolean;
  episode: PodcastEpisode | null;
  onClose: () => void;
  onSave: (payload: UpsertEpisodePayload) => void;
  saving: boolean;
}

const EpisodeDialog: React.FC<EpisodeDialogProps> = ({ open, episode, onClose, onSave, saving }) => {
  const [form, setForm] = useState<UpsertEpisodePayload>(episode ? episodeToForm(episode) : emptyForm());
  const [topicsInput, setTopicsInput] = useState((episode?.topics ?? []).join(", "));
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      const base = episode ? episodeToForm(episode) : emptyForm();
      setForm(base);
      setTopicsInput((episode?.topics ?? []).join(", "));
      setUploadError(null);
    }
  }, [open, episode]);

  const set = (field: keyof UpsertEpisodePayload, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const { upload_url, file_url } = await podcastEpisodesApi.createUploadUrl({
        filename: file.name,
        content_type: file.type || "application/octet-stream",
      });
      await fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      set("thumbnail_url", file_url);
    } catch {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = () => {
    const topics = topicsInput.split(",").map((t) => t.trim()).filter(Boolean);
    onSave({ ...form, topics });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{episode ? "Edit episode" : "New episode"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            fullWidth
            size="small"
            required
          />
          <TextField
            label="Description"
            value={form.description ?? ""}
            onChange={(e) => set("description", e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={3}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Guest name"
              value={form.guest_name ?? ""}
              onChange={(e) => set("guest_name", e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Guest title"
              value={form.guest_title ?? ""}
              onChange={(e) => set("guest_title", e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Episode number"
              type="number"
              value={form.episode_number ?? ""}
              onChange={(e) => set("episode_number", e.target.value === "" ? null : Number(e.target.value))}
              fullWidth
              size="small"
            />
            <TextField
              label="Display order"
              type="number"
              value={form.display_order ?? 0}
              onChange={(e) => set("display_order", Number(e.target.value))}
              fullWidth
              size="small"
            />
          </Stack>

          <Divider />

          {/* Thumbnail */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              Thumbnail image
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={form.thumbnail_url || undefined}
                variant="rounded"
                sx={{ width: 64, height: 64, bgcolor: "background.paper", flexShrink: 0 }}
              >
                <PodcastsIcon />
              </Avatar>
              <Stack spacing={0.75}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon />}
                >
                  {uploading ? "Uploading…" : "Upload image"}
                </Button>
                {form.thumbnail_url && (
                  <Button variant="text" size="small" color="error" onClick={() => set("thumbnail_url", "")}>
                    Remove
                  </Button>
                )}
              </Stack>
            </Stack>
            {uploadError && <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>{uploadError}</Alert>}
          </Box>

          <Divider />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Spotify URL"
              value={form.spotify_url ?? ""}
              onChange={(e) => set("spotify_url", e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="YouTube URL"
              value={form.youtube_url ?? ""}
              onChange={(e) => set("youtube_url", e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>

          <TextField
            label="Topics (comma-separated)"
            value={topicsInput}
            onChange={(e) => setTopicsInput(e.target.value)}
            fullWidth
            size="small"
            helperText="e.g. nutrition, recovery, sleep"
          />

          <FormControlLabel
            control={<Switch checked={form.is_active ?? true} onChange={(e) => set("is_active", e.target.checked)} />}
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || uploading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || uploading || !form.title.trim()}
        >
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// PodcastEpisodesTab
// ---------------------------------------------------------------------------

export const PodcastEpisodesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PodcastEpisode | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<PodcastEpisode | null>(null);
  const [search, setSearch] = useState("");

  const listQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => podcastEpisodesApi.list(),
    staleTime: 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: (payload: UpsertEpisodePayload) => podcastEpisodesApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setDialogOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<UpsertEpisodePayload> }) =>
      podcastEpisodesApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setDialogOpen(false);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => podcastEpisodesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setDeleteConfirm(null);
    },
  });

  const handleSave = (payload: UpsertEpisodePayload) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const allEpisodes = listQuery.data?.data ?? [];

  const filteredEpisodes = useMemo(() => {
    if (!search) return allEpisodes;
    const q = search.toLowerCase();
    return allEpisodes.filter(
      (ep) =>
        ep.title.toLowerCase().includes(q) ||
        (ep.guest_name ?? "").toLowerCase().includes(q) ||
        ep.topics.some((t) => t.toLowerCase().includes(q))
    );
  }, [allEpisodes, search]);

  return (
    <Stack spacing={2}>
      {/* Toolbar */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
        <TextField
          size="small"
          placeholder="Search episodes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          sx={{ flexShrink: 0 }}
          onClick={() => { setEditing(null); setDialogOpen(true); }}
        >
          New episode
        </Button>
      </Stack>

      {search && (
        <Typography variant="caption" color="text.secondary">
          {filteredEpisodes.length} of {allEpisodes.length} episodes
        </Typography>
      )}

      {/* List */}
      {listQuery.isLoading ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading episodes…</Typography>
        </Stack>
      ) : listQuery.isError ? (
        <Alert severity="error">Failed to load episodes.</Alert>
      ) : filteredEpisodes.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
          <PodcastsIcon sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
          <Typography variant="body2">
            {allEpisodes.length === 0 ? "No episodes yet." : "No episodes match your search."}
          </Typography>
          {allEpisodes.length === 0 && (
            <Typography variant="caption">Click "New episode" to add one.</Typography>
          )}
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {filteredEpisodes.map((ep) => (
            <EpisodeCard
              key={ep.id}
              ep={ep}
              onEdit={() => { setEditing(ep); setDialogOpen(true); }}
              onDelete={() => setDeleteConfirm(ep)}
              onToggle={(active) => updateMutation.mutate({ id: ep.id, payload: { is_active: active } })}
              toggling={updateMutation.isPending}
              deleting={deleteMutation.isPending}
            />
          ))}
        </Stack>
      )}

      <EpisodeDialog
        open={dialogOpen}
        episode={editing}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSave={handleSave}
        saving={isSaving}
      />

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Delete episode</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{deleteConfirm?.title}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
          >
            {deleteMutation.isPending ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
